import { FirebaseAuthChangeCallback, IthacaJwt } from '@dtx-company/ithaca-sdk/src/auth/types'
import {
  JSON_HEADERS,
  buildJwtFromFronteggAccessToken,
  getCurrentAccessTokenCookie,
  getCurrentImpersonationAccessTokenCookie,
  setAccessTokenCookie,
  useListenForFirebaseAuthChanges
} from '@dtx-company/ithaca-sdk/src'
import { OldIthacaJwtData, fetchClaimsFromIthaca } from '@dtx-company/ithaca-sdk/src/auth/ithaca'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { getCookieDomain } from '@dtx-company/true-common/src/utils/cookies/cookies.utils'
import { getIthacaRootUrl } from '@dtx-company/true-common/src/utils/urls/services'
import { handleDatadogLogger, logger } from '@dtx-company/logger'
import {
  setAuthChecked,
  setCurrentUserData,
  setImpersonatedUserData
} from '../../redux/slices/user-slice'
import { useAuthState } from '../useAuthState'
import { useCallback } from 'react'
import { useCohort } from '@dtx-company/inter-app/src/hooks/cohorts/useCohort'
import { useDispatch } from 'react-redux'
import { useFlowRouter } from '../useFlowRouter'
import { useRouter } from 'next/router'
import jwt_decode from 'jwt-decode'

export function useRestoreSession(): void {
  useListenForFirebaseAuthChanges(useRestoreSessionFromFirebaseOrFrontegg())
}

//we manually handle initializing the session on these routes
const DO_NOT_RESTORE_FRONTEGG_ROUTES = [
  Routes.FRONTEGG_SOCIAL_REDIRECT,
  Routes.FRONTEGG_SSO_REDIRECT,
  Routes.IMPERSONATION
]

function useShouldRestoreFronteggSession(): boolean {
  const isImpersonating = useAuthState().isImpersonating
  const currentRoute = useFlowRouter().pathname as Routes
  const shouldSkipFronteggSessionRestore = DO_NOT_RESTORE_FRONTEGG_ROUTES.includes(currentRoute)
  return !isImpersonating && !shouldSkipFronteggSessionRestore
}

export function useRestoreSessionFromFirebaseOrFrontegg(): FirebaseAuthChangeCallback {
  const dispatch = useDispatch()
  const shouldRestoreFronteggSession = useShouldRestoreFronteggSession()

  const restoreFirebaseSession = useRestoreFirebaseSession()
  const restoreFronteggSession = useRestoreFronteggSession()

  return useCallback<FirebaseAuthChangeCallback>(
    async user => {
      const IS_FLOWCODE_DOMAIN = window.location.href.includes(getCookieDomain())
      if (IS_FLOWCODE_DOMAIN) {
        if (user) {
          await restoreFirebaseSession(user)
        } else if (shouldRestoreFronteggSession) {
          await restoreFronteggSession()
        }
      }
      dispatch(setAuthChecked())
    },
    [dispatch, shouldRestoreFronteggSession, restoreFirebaseSession, restoreFronteggSession]
  )
}

function useRestoreFirebaseSession(): FirebaseAuthChangeCallback {
  const router = useRouter()
  const referrerIdParam = router.query.referrerId ?? router.query.referralId
  const referrerId = referrerIdParam ? String(referrerIdParam) : undefined
  const { name: cohortName } = useCohort()
  const dispatch = useDispatch()

  const firebaseAuthChangeCallback = useCallback<FirebaseAuthChangeCallback>(
    async user => {
      try {
        const firebaseToken = await user?.getIdTokenResult?.()

        const res = await fetch(`${getIthacaRootUrl()}/v1/verify?smalljwt=true`, {
          method: 'POST',
          headers: { ...JSON_HEADERS, Authorization: `Bearer ${firebaseToken?.token}` },
          body: JSON.stringify({
            referrerId,
            cohortName
          })
        })

        if (!res.ok) {
          await logger.logFetchError(res, { technicalArea: 'authentication' })
        }

        const json = await res.json()
        const rawJwt = json.token
        const decodedJwt = jwt_decode<OldIthacaJwtData>(rawJwt)
        const claims = await fetchClaimsFromIthaca(decodedJwt.ithacaId, true)

        if (!claims) {
          throw new Error('Failed to restore Firebase session - no user data returned from Ithaca')
        } else {
          const jwt: IthacaJwt = {
            ...claims,
            token: rawJwt
          }
          dispatch(setCurrentUserData({ data: jwt, isNewUser: false, isFronteggUser: false }))
          handleDatadogLogger(jwt?.ithacaId)
          await setAccessTokenCookie(rawJwt)
        }
      } catch (e) {
        logger.logError(e, { technicalArea: 'authentication' })
      }
    },
    [referrerId, dispatch, cohortName]
  )

  return firebaseAuthChangeCallback
}

export function useRestoreFronteggSession(): () => Promise<{ authenticated: boolean }> {
  const dispatch = useDispatch()

  return useCallback(async () => {
    try {
      const [accessToken, impersonationAccessToken] = await Promise.all([
        getCurrentAccessTokenCookie(),
        getCurrentImpersonationAccessTokenCookie()
      ])

      let currentUserJwt: IthacaJwt | undefined
      let impersonationJwt: IthacaJwt | undefined

      if (accessToken) {
        currentUserJwt = await buildJwtFromFronteggAccessToken(accessToken)
      }

      if (impersonationAccessToken) {
        impersonationJwt = await buildJwtFromFronteggAccessToken(impersonationAccessToken)
      }

      if (accessToken && !currentUserJwt) {
        throw new Error('Failed to restore Frontegg session - build JWT failed')
      }

      if (impersonationAccessToken && !impersonationJwt) {
        throw new Error('Failed to restore impersonation session - build JWT failed')
      }

      if (currentUserJwt) {
        dispatch(setCurrentUserData({ data: currentUserJwt, isFronteggUser: true }))
      }

      if (impersonationJwt) {
        dispatch(setImpersonatedUserData({ data: impersonationJwt }))
      }

      return { authenticated: Boolean(currentUserJwt) || Boolean(impersonationJwt) }
    } catch (e) {
      logger.logError(e, { technicalArea: 'authentication' })
      return { authenticated: false }
    }
  }, [dispatch])
}
